'use client';

import React, { useState } from 'react';
import { Modal } from '@/components/ui/Modal';
import { ModalBody } from '@/components/ui/ModalBody';
import { ModalFooter } from '@/components/ui/ModalFooter';
import { ExchangeIcon } from '@/svgs';
import { ExchangeDetailsModalProps } from '@/types';

export const ExchangeDetailsModal: React.FC<ExchangeDetailsModalProps> = ({
  isOpen,
  onClose,
  data,
}) => {
  const [isSwapped, setIsSwapped] = useState(false);
  const [isAnimating, setIsAnimating] = useState(false);

  const handleSwap = () => {
    setIsAnimating(true);
    setTimeout(() => {
      setIsSwapped(!isSwapped);
      setTimeout(() => {
        setIsAnimating(false);
      }, 50);
    }, 150);
  };

  const leftProduct = isSwapped ? data.productRequested : data.productOffered;
  const rightProduct = isSwapped ? data.productOffered : data.productRequested;
  const leftLabel = isSwapped ? 'Product Requested' : 'Product Offered';
  const rightLabel = isSwapped ? 'Product Offered' : 'Product Requested';

  const leftIsOffered = !isSwapped;
  const rightIsOffered = isSwapped;

  const calculateRatio = () => {
    const offered = data.productOffered.quantity;
    const requested = data.productRequested.quantity;
    if (isSwapped) {
      const ratio = offered / requested;
      return `1 : ${ratio.toFixed(1)}`;
    }
    const ratio = requested / offered;
    return `1 : ${ratio.toFixed(1)}`;
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="md">
      <div className="bg-[#0f1f4b] text-white p-6 rounded-t-2xl">
        <div className="flex items-start justify-between">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-[#F6D100] flex items-center justify-center">
              <ExchangeIcon className="w-5 h-5" />
            </div>
            <div>
              <h2 className="text-lg font-bold">Exchange Details</h2>
              <p className="text-xs text-blue-200 mt-0.5">
                TRANSACTION #{data.transactionId}
              </p>
            </div>
          </div>
          <div className="text-right">
            <p className="text-[10px] uppercase tracking-widest text-blue-200">Date</p>
            <p className="text-sm font-semibold mt-0.5">{data.date}</p>
          </div>
        </div>
      </div>

      <ModalBody className="py-6">
        <div className="bg-white border border-gray-100 rounded-xl p-5 shadow-sm">
          <div className="flex items-center gap-3 mb-4">
            <div className="w-10 h-10 rounded-full bg-[#F6D100] flex items-center justify-center">
              <span className="text-sm font-bold text-[#0f1f4b]">
                {data.requester.initials}
              </span>
            </div>
            <div>
              <p className="text-[10px] uppercase tracking-widest text-gray-400 font-medium">
                Requested By
              </p>
              <p className="text-sm font-bold text-[#0f1f4b]">
                {data.requester.name}
              </p>
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <p className="text-[10px] uppercase tracking-widest text-gray-400 font-medium">
                Contact Number
              </p>
              <p className="text-sm font-semibold text-[#0f1f4b] mt-0.5">
                {data.requester.contactNumber}
              </p>
            </div>
            <div>
              <p className="text-[10px] uppercase tracking-widest text-gray-400 font-medium">
                Location/City
              </p>
              <p className="text-sm font-semibold text-[#0f1f4b] mt-0.5">
                {data.requester.location}
              </p>
            </div>
          </div>
        </div>

        <div className="flex items-center gap-3 mt-6">

          <div
            className={`flex-1 bg-white p-4 pt-8 relative ${leftIsOffered ? '' : 'shadow-sm'}`}
            style={{
              borderRadius: '16px',
              border: leftIsOffered ? 'none' : '1.5px solid #93c5fd',
            }}
          >
            <div
              className="absolute flex items-center justify-center"
              style={{
                top: '-1px',
                right: '-1px',
                width: '36px',
                height: '36px',
                backgroundColor: leftIsOffered ? '#f472b6' : '#3b82f6',
                borderRadius: '0 16px 0 12px',
              }}
            >
              <svg
                className="w-3.5 h-3.5 text-white"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2.5}
                  d={leftIsOffered
                    ? "M5 10l7-7m0 0l7 7m-7-7v18"  
                    : "M19 14l-7 7m0 0l-7-7m7 7V3"   
                  }
                />
              </svg>
            </div>

            <p
              className="text-[10px] uppercase tracking-widest font-bold mb-3"
              style={{ color: leftIsOffered ? '#f472b6' : '#3b82f6' }}
            >
              {leftLabel}
            </p>

            <div className="flex items-center gap-2 mb-1">
              {leftIsOffered ? (
                <svg className="w-4 h-4 shrink-0" viewBox="0 0 16 16" fill="none">
                  <rect
                    x="8" y="1"
                    width="9" height="9"
                    rx="1.5"
                    transform="rotate(45 8 1)"
                    stroke="#f59e0b"
                    strokeWidth="1.5"
                    fill="none"
                  />
                </svg>
              ) : (
                <svg className="w-4 h-4 shrink-0" viewBox="0 0 16 16" fill="none">
                  <path d="M2 4h12M2 8h12M2 12h12" stroke="#3b82f6" strokeWidth="1.8" strokeLinecap="round"/>
                </svg>
              )}
              <span className="text-sm font-semibold text-[#0f1f4b]">
                {leftProduct.name}
              </span>
            </div>

            <p className="text-2xl font-extrabold text-[#0f1f4b] mt-1">
              {leftProduct.quantity.toLocaleString()}
              <span className="text-sm font-normal text-gray-400 ml-1">
                {leftProduct.unit}
              </span>
            </p>
          </div>

          <button
            type="button"
            onClick={handleSwap}
            className={`shrink-0 w-10 h-10 bg-white rounded-full flex items-center justify-center shadow-md border border-gray-100 hover:bg-gray-50 cursor-pointer transition-all duration-300 ${
              isAnimating ? 'rotate-180' : ''
            }`}
            title="Swap products"
          >
            <svg
              className="w-4 h-4 text-[#94a3b8]"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"
              />
            </svg>
          </button>

          <div
            className={`flex-1 bg-white p-4 pt-8 relative ${rightIsOffered ? '' : 'shadow-sm'}`}
            style={{
              borderRadius: '16px',
              border: rightIsOffered ? 'none' : '1.5px solid #93c5fd',
            }}
          >
            <div
              className="absolute flex items-center justify-center"
              style={{
                top: '-1px',
                right: '-1px',
                width: '36px',
                height: '36px',
                backgroundColor: rightIsOffered ? '#f472b6' : '#3b82f6',
                borderRadius: '0 16px 0 12px',
              }}
            >
              <svg
                className="w-3.5 h-3.5 text-white"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2.5}
                  d={rightIsOffered
                    ? "M5 10l7-7m0 0l7 7m-7-7v18"   
                    : "M19 14l-7 7m0 0l-7-7m7 7V3"   
                  }
                />
              </svg>
            </div>

            <p
              className="text-[10px] uppercase tracking-widest font-bold mb-3"
              style={{ color: rightIsOffered ? '#f472b6' : '#3b82f6' }}
            >
              {rightLabel}
            </p>

            <div className="flex items-center gap-2 mb-1">
              {rightIsOffered ? (
                <svg className="w-4 h-4 shrink-0" viewBox="0 0 16 16" fill="none">
                  <rect
                    x="8" y="1"
                    width="9" height="9"
                    rx="1.5"
                    transform="rotate(45 8 1)"
                    stroke="#f59e0b"
                    strokeWidth="1.5"
                    fill="none"
                  />
                </svg>
              ) : (
                <svg className="w-4 h-4 shrink-0" viewBox="0 0 16 16" fill="none">
                  <path d="M2 4h12M2 8h12M2 12h12" stroke="#3b82f6" strokeWidth="1.8" strokeLinecap="round"/>
                </svg>
              )}
              <span className="text-sm font-semibold text-[#0f1f4b]">
                {rightProduct.name}
              </span>
            </div>

            <p className="text-2xl font-extrabold text-[#0f1f4b] mt-1">
              {rightProduct.quantity.toLocaleString()}
              <span className="text-sm font-normal text-gray-400 ml-1">
                {rightProduct.unit}
              </span>
            </p>
          </div>
        </div>

        <div className="flex items-center justify-between mt-5 px-1">
          <span className="text-sm text-gray-400">Exchange Ratio</span>
          <span
            className="text-sm font-bold px-3 py-1 rounded-lg transition-all duration-300"
            style={{ backgroundColor: '#eff6ff', color: '#3b82f6' }}
          >
            {calculateRatio()}
          </span>
        </div>
      </ModalBody>

      <ModalFooter
        variant="fullWidthSubmit"
        onPrimary={onClose}
        primaryLabel="Close Details"
      />
    </Modal>
  );
};
